import { useState } from 'react';
import { indexAtOrBefore } from '@/core/util/search';
import { getTransport } from '@/state/playerStore';
import { useAnimationFrame } from './useAnimationFrame';

/**
 * How far (seconds) the position may step back across a boundary without moving the index back.
 * Browsers' reported output latency wobbles by a few milliseconds, so the heard position can dip
 * just behind a chord change for a frame; without this the previous chord flashes active again.
 */
const JITTER = 0.06;

/**
 * Index of the last time in `times` at or before `position`, ignoring clock wobble: a step back
 * of one index within JITTER of the boundary, or a position that isn't a number, keeps `current`.
 */
export function stableIndex(times: ArrayLike<number>, position: number, current: number): number {
  if (!Number.isFinite(position)) return current;
  const next = indexAtOrBefore(times, position);
  const boundary = times[current];
  if (next === current - 1 && boundary !== undefined && position > boundary - JITTER)
    return current;
  return next;
}

/**
 * Index of the last time in `times` at or before the heard playback position, as React state.
 * Re-renders only when the index changes (e.g. once per chord), not every frame.
 */
export function useTimeIndex(times: readonly number[], visualOffsetMs: number): number {
  const [index, setIndex] = useState(-1);
  useAnimationFrame(() => {
    const t = getTransport();
    const next = t ? stableIndex(times, t.getPosition(visualOffsetMs), index) : -1;
    if (next !== index) setIndex(next);
  });
  return index;
}
